import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Eye, Heart, Scale, Package } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useCompare } from '../context/CompareContext';
import { toast } from 'sonner';
import QuickViewModal from './QuickViewModal';
import { getPriceData } from '../utils/currency';

const ProductCard = memo(({ product }) => {
    const { addToCart } = useCart();
    const { addToCompare, removeFromCompare, isInCompare, canAddMore } = useCompare();
    const [showQuickView, setShowQuickView] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [imageError, setImageError] = useState(false);
    
    const inCompare = isInCompare(product.product_id);
    
    // Promo system annotation (from promotions engine) takes priority
    const sysPromo = product.promo;
    const hasSysPromoPrice = sysPromo?.promo_price != null && sysPromo.promo_price < (sysPromo.regular_price ?? product.price);
    // Check for promotional price first, then discount_percent
    const hasPromotion = !hasSysPromoPrice && product.promotional_price && product.promotional_price < product.price;
    const hasDiscount = !hasSysPromoPrice && !hasPromotion && product.discount_percent && product.discount_percent > 0;
    
    const finalPrice = hasSysPromoPrice
        ? sysPromo.promo_price
        : hasPromotion 
            ? product.promotional_price
            : hasDiscount 
                ? product.price * (1 - product.discount_percent / 100)
                : product.price;
    
    const isOnSale = hasSysPromoPrice || hasPromotion || hasDiscount;

    const handleAddToCart = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const success = await addToCart(product.product_id, 1);
        if (success) {
            toast.success('Добавено в количката', {
                description: product.name
            });
        } else {
            toast.error('Грешка при добавяне');
        }
    };

    const handleQuickView = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowQuickView(true);
    };

    const handleToggleCompare = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (inCompare) {
            removeFromCompare(product.product_id);
            toast.info('Премахнат от сравнение');
        } else if (canAddMore) {
            addToCompare(product);
            toast.success('Добавен за сравнение', {
                description: 'До 4 продукта могат да се сравняват'
            });
        } else {
            toast.error('Максимум 4 продукта за сравнение');
        }
    };

    // Get secondary image if available
    // Use thumbnail_url for faster loading, fallback to image_url
    const primaryImage = product.thumbnail_url || product.image_url;
    const secondaryImage = product.images?.[1] || primaryImage;
    const hasValidImage = primaryImage && !imageError;

    return (
        <>
            <Link 
                to={`/product/${product.product_id}`} 
                data-testid={`product-card-${product.product_id}`}
                className="card-product group block bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Image Container */}
                <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-700 dark:to-slate-600 rounded-t-2xl">
                    {hasValidImage ? (
                        <>
                            {/* Primary Image */}
                            <img 
                                src={primaryImage} 
                                alt={product.name}
                                className={`absolute inset-0 w-full h-full object-contain p-2 transition-all duration-700 ${
                                    isHovered && secondaryImage !== primaryImage 
                                        ? 'opacity-0 scale-110' 
                                        : 'opacity-100 group-hover:scale-105'
                                }`}
                                loading="lazy"
                                onError={() => setImageError(true)}
                            />
                            
                            {/* Secondary Image (on hover) */}
                            {secondaryImage !== primaryImage && (
                                <img 
                                    src={secondaryImage} 
                                    alt={product.name}
                                    className={`absolute inset-0 w-full h-full object-contain p-2 transition-all duration-700 ${
                                        isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                                    }`}
                                    loading="lazy"
                                />
                            )}
                        </>
                    ) : (
                        /* Placeholder when no image */
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Package className="w-16 h-16 text-amber-300" />
                        </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                        {product.is_new && (
                            <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg uppercase tracking-wider">
                                Ново
                            </span>
                        )}
                        {product.is_bestseller && (
                            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1">
                                <Star className="h-3 w-3 fill-white" />
                                Top
                            </span>
                        )}
                    </div>
                    
                    {(isOnSale || sysPromo) && (
                        <span className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg" data-testid={`promo-badge-${product.product_id}`}>
                            {sysPromo
                                ? sysPromo.badge
                                : hasPromotion 
                                    ? `-${Math.round((1 - product.promotional_price / product.price) * 100)}%`
                                    : `-${product.discount_percent}%`
                            }
                        </span>
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Quick Actions */}
                    <div className="absolute bottom-4 left-4 right-4 flex gap-2 translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                        <Button 
                            data-testid={`add-to-cart-${product.product_id}`}
                            onClick={handleAddToCart}
                            className="flex-1 bg-white hover:bg-amber-50 text-amber-700 shadow-xl text-sm py-2.5 rounded-xl font-semibold border border-amber-200"
                        >
                            <ShoppingCart className="h-4 w-4 mr-2" />
                            Добави
                        </Button>
                        <Button 
                            className="bg-white hover:bg-amber-50 text-amber-700 shadow-xl px-3 py-2.5 rounded-xl border border-amber-200"
                            onClick={handleQuickView}
                            data-testid={`quick-view-${product.product_id}`}
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                            className={`shadow-xl px-3 py-2.5 rounded-xl border ${
                                inCompare 
                                    ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600' 
                                    : 'bg-white hover:bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                            onClick={handleToggleCompare}
                            data-testid={`compare-${product.product_id}`}
                            title={inCompare ? 'Премахни от сравнение' : 'Добави за сравнение'}
                        >
                            <Scale className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Wishlist button (top right corner, always visible on hover) */}
                    <button 
                        className="absolute top-4 right-4 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg translate-x-2 group-hover:translate-x-0"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toast.info('Скоро: Любими продукти');
                        }}
                        style={{ display: isOnSale ? 'none' : 'flex' }}
                    >
                        <Heart className="h-4 w-4 text-slate-600 hover:text-red-500 transition-colors" />
                    </button>
                </div>
                
                {/* Content */}
                <div className="p-5">
                    {/* Name */}
                    <h3 className="font-semibold text-slate-800 dark:text-white mb-1 line-clamp-2 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug text-[15px]">
                        {product.name}
                    </h3>
                    
                    {/* Weight/Volume — shown only when admin confirms it's correct */}
                    {product.weight_visible && product.weight && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-2 flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {product.weight} {product.weight_unit || 'гр'}
                        </p>
                    )}
                    
                    {/* Price - Show Old and New price for promotions */}
                    <div className="flex flex-col gap-1 mb-4">
                        {isOnSale ? (
                            <>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-xl font-bold text-red-500 dark:text-red-400">
                                        {getPriceData(finalPrice).eurFormatted} €
                                    </span>
                                    <span className="text-sm text-slate-400 dark:text-slate-500 line-through">
                                        {getPriceData(product.price).eurFormatted} €
                                    </span>
                                </div>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="text-lg font-bold text-amber-700 dark:text-amber-400">
                                    {getPriceData(product.price).eurFormatted} €
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                </span>
                            </>
                        )}
                    </div>
                    
                    {/* Bottom Row */}
                    <div className="flex items-center justify-between pt-4 border-t border-amber-100 dark:border-slate-600">
                        {product.stock > 0 ? (
                            <span className="inline-flex items-center text-xs font-medium text-green-700 dark:text-green-400">
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                                В наличност
                            </span>
                        ) : (
                            <span className="inline-flex items-center text-xs font-medium text-slate-400 dark:text-slate-500">
                                <span className="w-2 h-2 bg-slate-300 dark:bg-slate-600 rounded-full mr-2" />
                                Изчерпано
                            </span>
                        )}
                        
                        {product.is_bestseller && (
                            <span className="inline-flex items-center text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg">
                                <Star className="h-3 w-3 fill-amber-400 text-amber-400 mr-1" />
                                Top
                            </span>
                        )}
                    </div>
                </div>
            </Link>

            {/* Quick View Modal */}
            <QuickViewModal 
                product={product} 
                isOpen={showQuickView} 
                onClose={() => setShowQuickView(false)} 
            />
        </>
    );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
